import {
    ArrowLeftIcon,
    ArrowRightIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CloudArrowUpIcon,
    DocumentTextIcon,
    MapIcon,
    PlusCircleIcon,
    SparklesIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { MOCK_DOCUMENTS, MOCK_PROVIDERS, MOCK_ROADMAP, PROVIDER_TYPE_LABEL } from './mockData'

type Stage = 'pick' | 'analyzing' | 'done'

// The demo always shows the same fictional new upload: ABA hours-change recommendation.
// Document doc_005 is what we pretend was just uploaded.
const DEMO_DOC = MOCK_DOCUMENTS.find((d) => d.id === 'doc_005') ?? MOCK_DOCUMENTS[0]
const DEMO_PROVIDER =
    MOCK_PROVIDERS.find((p) => p.id === DEMO_DOC.providerId) ?? MOCK_PROVIDERS[0]

export default function UploadAndAnalyze() {
    const [stage, setStage] = useState<Stage>('pick')
    const [showFindings, setShowFindings] = useState(false)

    useEffect(() => {
        if (stage !== 'analyzing') return
        const t = setTimeout(() => setStage('done'), 2400)
        return () => clearTimeout(t)
    }, [stage])

    return (
        <div className='max-w-4xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to='/admin/family-care-team-preview/team'
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                Back to care team
            </Link>

            <div className='mb-6'>
                <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Upload a document</h1>
                <p className='text-sm text-neutral-600 mt-1'>
                    Drop in an evaluation, care plan, IEP, or progress note. BrightPath will read it, pull out the
                    key information, and tell you what changes on your child&apos;s roadmap.
                </p>
            </div>

            {stage === 'pick' && <PickStage onStart={() => setStage('analyzing')} />}
            {stage === 'analyzing' && <AnalyzingStage />}
            {stage === 'done' && (
                <DoneStage
                    showFindings={showFindings}
                    toggleFindings={() => setShowFindings((v) => !v)}
                />
            )}
        </div>
    )
}

function PickStage({ onStart }: { onStart: () => void }) {
    return (
        <div className='space-y-4'>
            <div className='bg-white border-2 border-dashed border-brand-300 rounded-2xl p-10 text-center'>
                <CloudArrowUpIcon className='w-12 h-12 text-brand-500 mx-auto mb-3' />
                <p className='text-base font-semibold text-neutral-900'>Drop a PDF, Word doc, or image here</p>
                <p className='text-sm text-neutral-600 mt-1'>
                    or
                    <button
                        onClick={onStart}
                        className='inline-block mx-1 px-3 py-1 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded'
                    >
                        choose a file
                    </button>
                    to upload.
                </p>
                <p className='text-xs text-neutral-500 mt-3'>
                    Encrypted in transit and at rest. Only your family + the providers you explicitly share
                    with can read it.
                </p>
            </div>

            <div className='bg-white border border-neutral-200 rounded-xl p-4'>
                <div className='flex items-center gap-2 mb-2'>
                    <SparklesIcon className='w-4 h-4 text-brand-600' />
                    <span className='text-sm font-semibold text-neutral-900'>What BrightPath does next</span>
                </div>
                <ol className='space-y-1.5 text-xs text-neutral-600 list-decimal list-inside'>
                    <li>Identifies the document type and the provider it&apos;s from.</li>
                    <li>Extracts diagnoses, recommendations, goals, and cadence changes.</li>
                    <li>Compares them to what your other providers have already shared.</li>
                    <li>Updates the roadmap, or flags a conflict if something disagrees.</li>
                </ol>
            </div>
        </div>
    )
}

function AnalyzingStage() {
    const steps = [
        { label: 'Document uploaded', delay: 0 },
        { label: 'Identifying document type and provider', delay: 400 },
        { label: 'Extracting findings (diagnoses, goals, cadence)', delay: 900 },
        { label: 'Cross-checking against other provider data', delay: 1400 },
        { label: 'Updating roadmap and flagging conflicts', delay: 1900 },
    ]
    return (
        <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
            <div className='flex items-center gap-3 mb-4'>
                <div className='w-9 h-9 rounded-lg bg-brand-100 flex items-center justify-center'>
                    <SparklesIcon className='w-5 h-5 text-brand-700 animate-pulse' />
                </div>
                <div>
                    <p className='text-sm font-semibold text-neutral-900'>Analyzing {DEMO_DOC.fileName}</p>
                    <p className='text-xs text-neutral-500'>
                        BrightPath AI is reading your document. This usually takes a few seconds.
                    </p>
                </div>
            </div>
            <ul className='space-y-2'>
                {steps.map((s, i) => (
                    <AnalyzingStep key={s.label} label={s.label} delay={s.delay} isLast={i === steps.length - 1} />
                ))}
            </ul>
        </div>
    )
}

function AnalyzingStep({ label, delay, isLast }: { label: string; delay: number; isLast: boolean }) {
    const [done, setDone] = useState(false)
    useEffect(() => {
        const t = setTimeout(() => setDone(true), delay)
        return () => clearTimeout(t)
    }, [delay])
    return (
        <li className='flex items-center gap-3'>
            {done ? (
                <CheckCircleIcon className='w-5 h-5 text-green-600' />
            ) : (
                <div className='w-5 h-5 rounded-full border-2 border-brand-300 border-t-brand-600 animate-spin' />
            )}
            <span className={`text-sm ${done ? 'text-neutral-700' : 'text-neutral-500'}`}>
                {label}
                {!done && !isLast ? '...' : ''}
            </span>
        </li>
    )
}

function DoneStage({
    showFindings,
    toggleFindings,
}: {
    showFindings: boolean
    toggleFindings: () => void
}) {
    const milestonesAffected = MOCK_ROADMAP.filter((m) => DEMO_DOC.milestoneIds.includes(m.id))
    return (
        <div className='space-y-4'>
            {/* High-level summary */}
            <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
                <div className='flex items-start gap-3 mb-3'>
                    <div className='w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0'>
                        <DocumentTextIcon className='w-5 h-5 text-brand-600' />
                    </div>
                    <div className='flex-1 min-w-0'>
                        <p className='text-xs uppercase tracking-wider text-brand-700 font-semibold'>
                            Document analyzed
                        </p>
                        <h2 className='text-lg font-semibold text-neutral-900'>{DEMO_DOC.title}</h2>
                        <p className='text-sm text-neutral-600 mt-0.5'>
                            From {DEMO_PROVIDER.clinicianName} ({PROVIDER_TYPE_LABEL[DEMO_PROVIDER.type]})
                        </p>
                    </div>
                </div>
                <div className='p-3 bg-brand-50/50 border border-brand-100 rounded-lg'>
                    <div className='flex items-center gap-1.5 mb-1.5'>
                        <SparklesIcon className='w-3.5 h-3.5 text-brand-700' />
                        <span className='text-[10px] uppercase tracking-wider text-brand-700 font-semibold'>
                            BrightPath summary
                        </span>
                    </div>
                    <p className='text-sm text-neutral-800'>{DEMO_DOC.summary}</p>
                </div>

                <button
                    onClick={toggleFindings}
                    className='mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800'
                >
                    {showFindings ? (
                        <>
                            <ChevronUpIcon className='w-4 h-4' />
                            Hide extracted findings
                        </>
                    ) : (
                        <>
                            <ChevronDownIcon className='w-4 h-4' />
                            See {DEMO_DOC.findings.length} extracted findings
                        </>
                    )}
                </button>

                {showFindings && (
                    <div className='mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2'>
                        {DEMO_DOC.findings.map((f) => (
                            <div
                                key={f.label}
                                className='bg-white border border-neutral-200 rounded-lg p-3'
                            >
                                <div className='flex items-center justify-between gap-2 mb-1'>
                                    <span className='text-[10px] uppercase tracking-wider text-neutral-500'>
                                        {f.label}
                                    </span>
                                    {f.impactsRoadmap && (
                                        <span
                                            className={
                                                'inline-flex items-center gap-0.5 text-[10px] text-brand-700 ' +
                                                'bg-brand-50 px-1.5 py-0.5 rounded'
                                            }
                                        >
                                            <SparklesIcon className='w-2.5 h-2.5' />
                                            Roadmap
                                        </span>
                                    )}
                                </div>
                                <p className='text-sm text-neutral-900'>{f.value}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Impact on roadmap */}
            <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
                <div className='flex items-center gap-2 mb-3'>
                    <MapIcon className='w-5 h-5 text-brand-600' />
                    <h2 className='text-lg font-semibold text-neutral-900'>Impact on the roadmap</h2>
                </div>
                <p className='text-sm text-neutral-700 mb-4'>
                    This document affects {milestonesAffected.length} milestone
                    {milestonesAffected.length === 1 ? '' : 's'}. Because it conflicts with another provider&apos;s
                    recommendation, BrightPath has paused the roadmap update and opened a clarifying conversation.
                </p>
                <ul className='space-y-2 mb-4'>
                    {milestonesAffected.map((m) => (
                        <li
                            key={m.id}
                            className='flex items-start gap-2 p-3 bg-neutral-50 border border-neutral-200 rounded-lg'
                        >
                            <PlusCircleIcon className='w-4 h-4 text-brand-600 mt-0.5 flex-shrink-0' />
                            <div className='flex-1 min-w-0'>
                                <span className='text-sm font-medium text-neutral-900'>{m.title}</span>
                                <span className='text-xs text-neutral-500 ml-2'>{m.sequence}</span>
                            </div>
                        </li>
                    ))}
                </ul>
                <div className='flex flex-wrap gap-2'>
                    <Link
                        to='/admin/family-care-team-preview/conflicts/conf_001'
                        className='inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg'
                    >
                        Resolve conflict
                        <ArrowRightIcon className='w-4 h-4' />
                    </Link>
                    <Link
                        to='/admin/family-care-team-preview/roadmap'
                        className={
                            'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium ' +
                            'text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg'
                        }
                    >
                        See full roadmap update
                        <ArrowRightIcon className='w-4 h-4' />
                    </Link>
                </div>
            </div>
        </div>
    )
}
